import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, RawAxiosRequestHeaders } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import config from '../config/config';
import { auth } from '../firebase';
import { getCsrfToken } from './csrfUtils';

const headers: RawAxiosRequestHeaders = {
  'Content-Type': 'application/json',
  'x-trace-id': uuidv4(),
};

/**
 * Factory function to create axios instances with common configuration
 *
 * CSRF Protection:
 * Each instance is configured to automatically handle CSRF tokens for security.
 * - withCredentials: true - Enables cookie transmission (required for CSRF token)
 * - xsrfCookieName: 'XSRF-TOKEN' - Cookie name where backend sets the CSRF token
 * - xsrfHeaderName: 'x-xsrf-token' - Header name where Axios sends the CSRF token
 *
 * Axios will automatically:
 * 1. Read the CSRF token from the XSRF-TOKEN cookie
 * 2. Add it as x-xsrf-token header on POST/PUT/DELETE/PATCH requests
 * 3. Send credentials (cookies) with every request
 */
const createAxiosInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    headers: {
      ...headers,
    },
    // Enable credentials to allow cookies (including CSRF token cookie)
    withCredentials: true,
  });

  // Configure automatic CSRF token handling
  instance.defaults.xsrfCookieName = 'XSRF-TOKEN'; // Cookie name set by backend
  instance.defaults.xsrfHeaderName = 'x-xsrf-token'; // Header name expected by backend

  const requestInterceptor = async (request: InternalAxiosRequestConfig) => {
    request.headers['x-idToken'] = (await auth?.currentUser?.getIdToken()) || localStorage.getItem('token');
    request.headers['x-user_id'] = localStorage.getItem('user_id') || '';
    request.headers['x-department'] = localStorage.getItem('department') || '';
    request.headers.user = localStorage.getItem('email') || '';

    // Manually add CSRF token for POST/PUT/DELETE/PATCH requests
    // Note: The XSRF-TOKEN cookie must be non-HttpOnly for JavaScript to read it.
    // This manual approach ensures the token is added even when Axios automatic handling doesn't trigger.
    const method = request.method?.toUpperCase();
    if (method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        request.headers['x-xsrf-token'] = csrfToken;
      }
    }

    return request;
  };

  const responseSuccessInterceptor = (response: AxiosResponse) => Promise.resolve(response.data);

  const responseErrorInterceptor = (err: AxiosError) => {
    if (err?.response?.data) {
      const responseData = err.response.data as any;

      const validationMessage = Array.isArray(responseData?.error) && responseData.error.length > 0 && typeof responseData.error[0]?.msg === 'string' ? responseData.error[0].msg : null;

      const fallbackMessage = typeof responseData?.message === 'string' ? responseData.message : null;
      const networkMessage = typeof err?.message === 'string' ? err.message : null;

      return Promise.reject(new Error(validationMessage || fallbackMessage || networkMessage || 'Something went wrong'));
    }
    return Promise.reject(new Error(err?.message || 'Something went wrong'));
  };

  instance.interceptors.request.use(requestInterceptor);
  instance.interceptors.response.use(responseSuccessInterceptor, responseErrorInterceptor);

  return instance;
};

// Create axios instance for PMS API
const CommHandler: AxiosInstance = createAxiosInstance(config.apiBaseUrl);

// Create axios instance for RBAC API
export const RbacCommHandler: AxiosInstance = createAxiosInstance(config.rbacApiBaseUrl);

export default CommHandler;
